/**
 * useResponsiveMode — B-2026-0056
 *
 * Returns a reactive description of the current viewport breakpoint and
 * orientation. Used by overlay components to switch between desktop side-panel,
 * mobile portrait pull-up sheet, and mobile landscape collapsed-pill layouts.
 *
 * Breakpoint thresholds (mirrors Tailwind v3/v4 defaults):
 *   sm  ≥ 640px
 *   md  ≥ 768px
 *   lg  ≥ 1024px
 *   xl  ≥ 1280px
 *
 * Orientation detection priority:
 *   1. `window.screen.orientation.type` (most reliable on mobile)
 *   2. `matchMedia("(orientation: landscape)")` fallback
 *
 * The hook subscribes to both `window resize` events and the
 * `screen.orientation change` event so it stays in sync on all devices.
 */

import { useEffect, useState } from 'react';

export type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

export interface ResponsiveMode {
  /** true when viewport width >= 1024px (Tailwind `lg` breakpoint). */
  isDesktop: boolean;
  /** true when viewport width < 768px (below Tailwind `md` breakpoint). */
  isMobile: boolean;
  /** true when the device is in landscape orientation. */
  isLandscape: boolean;
  /** Finest breakpoint that the current viewport width satisfies. */
  breakpoint: Breakpoint;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBreakpoint(width: number): Breakpoint {
  if (width >= 1280) return 'xl';
  if (width >= 1024) return 'lg';
  if (width >= 768) return 'md';
  return 'sm';
}

function detectLandscape(): boolean {
  // Prefer the Screen Orientation API (more reliable on mobile browsers).
  if (
    typeof window !== 'undefined' &&
    window.screen?.orientation?.type
  ) {
    return window.screen.orientation.type.startsWith('landscape');
  }
  // Fallback: CSS media query.
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(orientation: landscape)').matches;
  }
  return false;
}

function buildMode(): ResponsiveMode {
  if (typeof window === 'undefined') {
    // SSR / test environments without a real window.
    return { isDesktop: true, isMobile: false, isLandscape: false, breakpoint: 'lg' };
  }
  const width = window.innerWidth;
  const breakpoint = getBreakpoint(width);
  return {
    isDesktop: width >= 1024,
    isMobile: width < 768,
    isLandscape: detectLandscape(),
    breakpoint,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useResponsiveMode(): ResponsiveMode {
  const [mode, setMode] = useState<ResponsiveMode>(buildMode);

  useEffect(() => {
    function update() {
      setMode(buildMode());
    }

    window.addEventListener('resize', update);

    // Screen orientation change fires before/after the viewport reflows on
    // mobile — subscribe so landscape ↔ portrait transitions are instant.
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener('change', update);
    }

    // matchMedia orientation fallback for browsers without Screen Orientation API.
    const mql = window.matchMedia?.('(orientation: landscape)');
    mql?.addEventListener('change', update);

    return () => {
      window.removeEventListener('resize', update);
      window.screen?.orientation?.removeEventListener?.('change', update);
      mql?.removeEventListener('change', update);
    };
  }, []);

  return mode;
}
